import NextLink from 'next/link';
import Avatar from '@savvy-kit/components/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@savvy-kit/components/dropdown-menu';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@savvy-kit/templates/sidebar';
import { Check, ChevronsUpDown, LogOut, Settings } from 'lucide-react';

import { useTrackEvent } from '@savvy/deprecated-shared/tracking/use-track-event';

import type { ClientPortalAccountMenuState } from './types';
import {
  CLIENT_PORTAL_ACCOUNT_ROUTE,
  CLIENT_PORTAL_NAV_BUTTON_CLASS,
  CLIENT_PORTAL_SETTINGS_LABEL,
  CLIENT_PORTAL_SIGN_OUT_LABEL,
  CLIENT_PORTAL_SWITCH_HOUSEHOLD_LABEL,
  CLIENT_PORTAL_USER_MENU_ELEMENT_NAMES,
} from './constants';
import { isPresentString } from './utils';

type ClientPortalUserMenuProps = {
  readonly clientName: string;
  readonly clientEmail: string | null;
  readonly clientAvatarUrl: string | null;
  readonly isAccountActive: boolean;
  readonly accountMenu: ClientPortalAccountMenuState;
};

export function ClientPortalUserMenu({
  clientName,
  clientEmail,
  clientAvatarUrl,
  isAccountActive,
  accountMenu,
}: ClientPortalUserMenuProps) {
  const trackInteraction = useTrackEvent('userInteraction');
  const { state } = useSidebar();
  const isCollapsed = state === 'collapsed';

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <SidebarMenuButton
                size="lg"
                isActive={isAccountActive}
                aria-label={clientName}
                tooltip={clientName}
                className={cn(CLIENT_PORTAL_NAV_BUTTON_CLASS, 'gap-2.5')}
              />
            }
          >
            <Avatar showFallbackIcon className="size-8" imageUrl={clientAvatarUrl} />
            {!isCollapsed && (
              <>
                <Text variant="subtitle-sm" className="min-w-0 flex-1 truncate">
                  {clientName}
                </Text>
                <ChevronsUpDown className="size-4 shrink-0" />
              </>
            )}
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-(--anchor-width) min-w-56" align="start" side="top">
            <div className="px-2 py-1.5">
              <Text variant="subtitle-sm" className="truncate">
                {clientName}
              </Text>
              {isPresentString(clientEmail) && (
                <Text variant="caption" className="truncate">
                  {clientEmail}
                </Text>
              )}
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              render={
                <NextLink
                  href={CLIENT_PORTAL_ACCOUNT_ROUTE}
                  onClick={() => {
                    trackInteraction({
                      elementName: CLIENT_PORTAL_USER_MENU_ELEMENT_NAMES.settings,
                      htmlElement: 'link',
                      interactionType: 'button_click',
                    });
                  }}
                />
              }
            >
              <Settings />
              {CLIENT_PORTAL_SETTINGS_LABEL}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                trackInteraction({
                  elementName: CLIENT_PORTAL_USER_MENU_ELEMENT_NAMES.logout,
                  htmlElement: 'button',
                  interactionType: 'button_click',
                });
                accountMenu.signOut();
              }}
            >
              <LogOut />
              {CLIENT_PORTAL_SIGN_OUT_LABEL}
            </DropdownMenuItem>
            {accountMenu.canSwitchHousehold && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>{CLIENT_PORTAL_SWITCH_HOUSEHOLD_LABEL}</DropdownMenuLabel>
                {accountMenu.households.map((household) => {
                  const isActiveHousehold = household.id === accountMenu.activeHouseholdId;
                  return (
                    <DropdownMenuItem
                      key={household.id}
                      disabled={accountMenu.switching}
                      aria-current={isActiveHousehold ? true : undefined}
                      onClick={() => {
                        if (isActiveHousehold) {
                          return;
                        }
                        trackInteraction({
                          elementName: CLIENT_PORTAL_USER_MENU_ELEMENT_NAMES.switchHousehold,
                          htmlElement: 'button',
                          interactionType: 'button_click',
                        });
                        accountMenu.selectHousehold(household.id);
                      }}
                    >
                      <span className="min-w-0 flex-1 truncate">{household.name}</span>
                      {isActiveHousehold && <Check className="size-4 shrink-0 text-savvy-green" />}
                    </DropdownMenuItem>
                  );
                })}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
